import { useState, useMemo, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import API from "../../api";
import { capture } from "../../instrument";
import toast from "react-hot-toast";
import { NON_COMMUNIQUE } from "../../utils";

const formatAnswer = (questionRecord) => {
  const { answerType, answer } = questionRecord;

  if (!answer) return null;
  if (answer === NON_COMMUNIQUE) return "Non communiqué";

  switch (answerType) {
    case "MULTIPLE_CHOICE": {
      const label = questionRecord.options?.find((o) => o.optionId?.toString() === answer?.toString())?.label;
      return label || answer;
    }
    case "DATE": {
      const date = new Date(answer);
      if (isNaN(date.getTime())) return answer;
      return date.toLocaleDateString("fr-FR");
    }
    case "HOUR":
      return answer;
    case "TEXT": {
      if (answer.length > 80) return `${answer.substring(0, 80)}...`;
      return answer;
    }
    case "DOCUMENT":
      return "Document joint";
    default:
      return answer;
  }
};

const DuplicateAdmissionModal = ({ admissionId, onClose, getAdmissionDetails, setSelectedAdmissions }) => {
  const [admission, setAdmission] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [followsParent, setFollowsParent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAdmission = async () => {
      setLoading(true);
      try {
        const { ok, data } = await API.get(`/admin/admission/${admissionId}`);
        if (ok === false) throw new Error("Failed to fetch admission");
        setAdmission(data);
      } catch (error) {
        capture(error);
        toast.error("Une erreur est survenue lors de la recuperation du dossier");
        onClose();
      }
      setLoading(false);
    };

    fetchAdmission();
  }, [admissionId]);

  const answeredQuestions = useMemo(() => {
    if (!admission?.questionRecords) return [];
    return admission.questionRecords.filter((qr) => qr.answer && qr.answeredAt);
  }, [admission?.questionRecords]);

  useEffect(() => {
    if (answeredQuestions.length > 0) {
      setSelectedIds(new Set(answeredQuestions.map((qr) => qr._id)));
    }
  }, [answeredQuestions]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleDuplicateConfirm = async (selectedQuestionRecordIds) => {
    setLoading(true);
    try {
      const { ok, data } = await API.post(`/admin/admission/${admissionId}/duplicate`, {
        selectedQuestionRecordIds,
        followsParent,
      });
      if (!ok) throw new Error("Failed to duplicate admission");

      toast.success("Dossier dupliqué avec succès");
      await getAdmissionDetails(data._id);
      setSelectedAdmissions(new Set());
      onClose();
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de la duplication du dossier");
    }
    setLoading(false);
  };

  const selectAll = () => setSelectedIds(new Set(answeredQuestions.map((qr) => qr._id)));
  const selectNone = () => setSelectedIds(new Set());

  const selectedCount = selectedIds.size;
  const totalCount = answeredQuestions.length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base font-semibold text-gray-900">Copier les réponses</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <IoClose className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading === true ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : totalCount === 0 ? (
            <div className="py-12 px-5 text-center text-sm text-gray-500">Aucune reponse a copier</div>
          ) : (
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500">
                  {selectedCount}/{totalCount} selectionnee{selectedCount > 1 ? "s" : ""}
                </span>
                <div className="flex gap-3 text-sm">
                  {totalCount !== selectedCount ? (
                    <button onClick={selectAll} disabled={loading} className="text-primary-600 hover:text-primary-700 disabled:text-gray-400">
                      Sélectionner tout
                    </button>
                  ) : (
                    <button onClick={selectNone} disabled={loading} className="text-primary-600 hover:text-primary-700 disabled:text-gray-400">
                      Déselectionner tout
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                {answeredQuestions.map((qr) => {
                  const isSelected = selectedIds.has(qr._id);
                  const answer = formatAnswer(qr);

                  return (
                    <label key={qr._id} className={`flex gap-3 p-3 rounded cursor-pointer transition-colors ${isSelected === true ? "bg-primary-50" : "hover:bg-gray-50"}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(qr._id)}
                        disabled={loading}
                        className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900">{qr.question || ""}</div>
                        {answer && <div className="text-sm text-gray-500 mt-0.5 truncate">{answer}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex flex-col gap-3">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={followsParent}
              onChange={(e) => setFollowsParent(e.target.checked)}
              disabled={loading}
              className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Envoyer en saisie en même temps que le dossier source</span>
          </label>
          <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md transition-colors">
            Annuler
          </button>
          <button
            onClick={() => handleDuplicateConfirm(Array.from(selectedIds))}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Dupliquer
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateAdmissionModal;
